import React from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Spinner } from "reactstrap";
import { useTranslation } from "react-i18next";
import { getQueryLogs } from "../SummaryResultsModal/Utility/utility";
import { useVademecumStore } from "../../../../../Stores/vademecumStore";
import { useAuth } from "../../../../../Services/auth";
import { QueryHistoryItemLogModalItem, hasRenderableContent } from "./QueryHistoryItemLogModalItem";
import TenderTitleComponent from "../SummaryResultsModal/Components/tenderTitleComponent";

const isPlainObject = (value) => typeof value === "object" && value !== null && !Array.isArray(value);

const PROMPT_KEYS = ["prompt", "request_prompt", "ai_prompt", "prompt_text", "prompt_body"];
const RESULT_KEYS = ["result", "response", "ai_result", "value", "answer", "completion"];

const findFirstMatchByKeys = (value, keys, path = []) => {
  if (value === null || value === undefined) {
    return null;
  }

  if (Array.isArray(value)) {
    for (let idx = 0; idx < value.length; idx += 1) {
      const result = findFirstMatchByKeys(value[idx], keys, [...path, `[${idx}]`]);
      if (result) {
        return result;
      }
    }
    return null;
  }

  if (isPlainObject(value)) {
    for (const key of Object.keys(value)) {
      if (keys.includes(key)) {
        return {
          key,
          value: value[key],
          path: [...path, key],
        };
      }
    }

    for (const [childKey, childValue] of Object.entries(value)) {
      const result = findFirstMatchByKeys(childValue, keys, [...path, childKey]);
      if (result) {
        return result;
      }
    }
  }

  return null;
};

const omitTopLevelKeys = (value, matches) => {
  if (!isPlainObject(value)) {
    return value;
  }

  const clone = { ...value };
  matches.forEach((match) => {
    if (match?.path?.length === 1) {
      delete clone[match.path[0]];
    }
  });
  return clone;
};

const createPreviewMatch = (preview, keys) => {
  if (!preview || typeof preview !== "object") {
    return null;
  }

  for (const key of keys) {
    if (Object.prototype.hasOwnProperty.call(preview, key)) {
      const value = preview[key];
      if (
        value !== null &&
        value !== undefined &&
        !(typeof value === "string" && value.trim().length === 0)
      ) {
        return {
          key,
          value,
          path: [key],
          fromPreview: true,
        };
      }
    }
  }

  return null;
};

export default function QueryHistoryItemLogModal() {
  const { t } = useTranslation();
  const auth = useAuth();

  React.useEffect(() => {
    getQueryLogs(auth);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [
    status,
    error,
    modalVisible,
    setModalVisible,
    logData,
    logId,
    logName,
    preview,
    clearPreview,
  ] = useVademecumStore((state) => [
    state.queryHistoryItemLogStatus,
    state.queryHistoryItemLogError,
    state.queryHistoryItemLogModalVisible,
    state.setQueryHistoryItemLogModalVisible,
    state.queryHistoryItemLog,
    state.queryHistoryItemLogToBeShown,
    state.queryHistoryItemLogName,
    state.queryHistoryItemLogPreview,
    state.clearQueryHistoryItemLogPreview,
  ]);

  const promptMatch = React.useMemo(() => findFirstMatchByKeys(logData, PROMPT_KEYS), [logData]);
  const resultMatch = React.useMemo(() => findFirstMatchByKeys(logData, RESULT_KEYS), [logData]);

  const previewPromptMatch = React.useMemo(() => createPreviewMatch(preview, PROMPT_KEYS), [preview]);
  const previewResultMatch = React.useMemo(() => createPreviewMatch(preview, RESULT_KEYS), [preview]);

  const promptSection = promptMatch ?? previewPromptMatch;
  const resultSection = resultMatch ?? previewResultMatch;

  const logContent = React.useMemo(
    () => omitTopLevelKeys(logData, [promptMatch, resultMatch]),
    [logData, promptMatch, resultMatch]
  );

  const promptKeyEntry = React.useMemo(() => {
    if (!isPlainObject(logContent)) {
      return null;
    }
    const entries = Object.entries(logContent);
    return entries.length === 1 ? entries[0] : null;
  }, [logContent]);

  const promptKey = promptKeyEntry?.[0] ?? null;

  const promptValue = React.useMemo(() => (promptKeyEntry ? promptKeyEntry[1] : logContent), [promptKeyEntry, logContent]);

  const renderContent = React.useCallback(() => {
    if (promptKeyEntry) {
      if (!hasRenderableContent(promptValue)) {
        return <div>{t("No data found")}</div>;
      }
      return <QueryHistoryItemLogModalItem value={promptValue} />;
    }

    if (!hasRenderableContent(logContent)) {
      return <div>{t("No data found")}</div>;
    }

    if (isPlainObject(logContent)) {
      return Object.entries(logContent).map(([key, value]) => (
        <QueryHistoryItemLogModalItem key={key} label={key} value={value} />
      ));
    }

    return <QueryHistoryItemLogModalItem value={logContent} />;
  }, [promptKeyEntry, promptValue, logContent, t]);

  const renderValueSection = React.useCallback(
    (label, match) => {
      if (!match || !hasRenderableContent(match.value)) {
        return null;
      }

      const path =
        match.path && match.path.length > 0
          ? match.path
              .map((segment) => {
                if (segment.startsWith("[")) {
                  return segment;
                }
                return segment;
              })
              .join(" > ")
          : null;

      const headerText = path ? `${label} (${path})` : label;
      const value = match.value;

      return (
        <div className="d-flex flex-column tc-flex-gap-5px">
          <div className="fw-bold">{headerText}</div>
          {typeof value === "string" ? (
            <pre className="bg-light rounded p-2 mb-0 small overflow-auto">{value}</pre>
          ) : (
            <QueryHistoryItemLogModalItem value={value} />
          )}
        </div>
      );
    },
    []
  );

  const closeModal = React.useCallback(() => {
    setModalVisible(false);
    clearPreview();
  }, [setModalVisible, clearPreview]);

  return (
    <Modal
      isOpen={modalVisible}
      role="dialog"
      autoFocus
      centered
      className="tc-screen-width-modal"
      tabIndex="-1"
      zIndex="11"
      toggle={closeModal}
    >
      <ModalHeader tag="h1" className="ta-modals-header" toggle={closeModal}>
        <div className="d-flex flex-column">
          <div className="tc-modal-header">
            {`${t("Logs for query")}: ${logId} (${logName})`}
          </div>
          <TenderTitleComponent />
        </div>
      </ModalHeader>
      <ModalBody>
        <div className="tc-template-modal-max-height-tallest tc-text-muted">
          {status === "loading" ? (
            <Spinner />
          ) : (
            <div className="d-flex flex-column tc-flex-gap-10px">
              {status === "error" ? (
                <div className="d-flex flex-column tc-flex-gap-10px">
                  <div className="fw-bold tc-formerror-colour-only">
                    {t("This log was either not found, or another error occurred")}:
                  </div>
                  <div>{error?.message}</div>
                </div>
              ) : (
                <React.Fragment>
                  <div className="fs-1 me-auto ms-auto fw-bold">{t("Diagnostic Info")}</div>
                  {renderValueSection(t("Prompt"), promptSection)}
                  {renderValueSection(t("Result"), resultSection)}
                  {promptKey ? (
                    <div className="d-flex flex-column tc-flex-gap-5px">
                      <div className="fw-bold">{t("Prompt Key")}:</div>
                      <div className="tc-break-word-anywhere-flex">{promptKey}</div>
                    </div>
                  ) : null}
                  <div className="d-flex flex-column tc-flex-gap-15px">{renderContent()}</div>
                </React.Fragment>
              )}
            </div>
          )}
        </div>
      </ModalBody>
      <ModalFooter className="d-flex justify-content-between">
        <Button className="tc-full-width-small" onClick={closeModal} type="button" color="danger">
          {t("Back")}
        </Button>
      </ModalFooter>
    </Modal>
  );
}
